import { fetchServer } from "@/features/utils/fetch";
import type {
  Document,
  DocumentType,
  LegalDocument,
  ResponseUpdateKyc,
  UploadDocument
} from "../types";
import { StandardResponse } from "@/features/utils/types";

export class DocumentApiClient {
  static async getDocuments(UUID: string, id?: string): Promise<Document[]> {
    const response = await fetchServer(
      !id ? "/document/list" : `/document/list?contextLegalEntityUuid=${id}`,
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: { UUID }
      }
    );

    if (!response.ok) throw new Error("Error");

    const res = await response.json();
    return res;
  }

  static async getDocumentsDetails(
    UUID: string,
    legalEntityUuid?: string | null,
    isAgentLe?: boolean
  ): Promise<Document[]> {
    const response = await fetchServer(
      !legalEntityUuid
        ? `/document/list_details/${UUID}`
        : isAgentLe
          ? `/document/list_details/${UUID}?contextLegalEntityUuid=${legalEntityUuid}&isAgentLe=${isAgentLe}`
          : `/document/list_details/${UUID}?contextLegalEntityUuid=${legalEntityUuid}`,
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );

    if (!response.ok) throw new Error("Error");

    return await response.json();
  }

  static async getDocumentsChartCompany(id: string): Promise<LegalDocument> {
    const response = await fetchServer(`/company/${id}/docs`, {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) throw new Error("Error");

    return await response.json();
  }

  static async getDocumentDetail(
    UUID: string,
    algoregDocumentId: string,
    id?: string
  ): Promise<Document[]> {
    const response = await fetchServer(
      !id
        ? `/document/details/${UUID}/${algoregDocumentId}`
        : `/document/details/${UUID}/${algoregDocumentId}?contextLegalEntityUuid=${id}`,
      {
        method: "GET",
        reloadOn401: false,
        mainRoot: false
      }
    );

    if (!response.ok) throw new Error("Error");

    return await response.json();
  }

  static async getDocumentContent(
    UUID: string,
    algoregDocumentId: string,
    id?: string,
    isAgentLe?: boolean
  ): Promise<Blob> {
    const response = await fetchServer(
      !id
        ? "/document/download"
        : !isAgentLe
          ? `/document/download?contextLegalEntityUuid=${id}`
          : `/document/download?contextLegalEntityUuid=${id}&isAgentLe=${isAgentLe}`,
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: { UUID, algoregDocumentId }
      }
    );

    if (!response.ok) throw new Error("Error");

    return await response.blob();
  }

  static async updateUserUnderAudit(): Promise<StandardResponse<ResponseUpdateKyc>> {
    const response = await fetchServer("/user/kyc_status_update", {
      method: "PUT",
      reloadOn401: false,
      mainRoot: false,
      body: {}
    });

    if (!response.ok) throw new Error("Error");

    return await response.json();
  }

  static async getDocumentTypes(): Promise<DocumentType[]> {
    const response = await fetchServer("/document/typesByCateg/2", {
      method: "GET",
      reloadOn401: false,
      mainRoot: false
    });

    if (!response.ok) throw new Error("Error");

    return await response.json();
  }
  static async uploadDocumentChart(
    document: LegalDocument,
    id?: string
  ): Promise<{ success: boolean }> {
    const response = await fetchServer(`/company/${id}/docs`, {
      method: "POST",
      reloadOn401: false,
      mainRoot: false,
      body: {
        name: document.name,
        base64docs: document.base64docs
      }
    });
    if (!response.ok) throw new Error("Error");
    return await response.json();
  }

  static async uploadDocument(
    document: UploadDocument,
    id?: string
  ): Promise<{ session_id: string }> {
    const response = await fetchServer(
      !id ? "/document/upload" : `/document/upload?contextLegalEntityUuid=${id}`,
      {
        method: "POST",
        reloadOn401: false,
        mainRoot: false,
        body: document
      }
    );
    if (!response.ok) throw new Error("Error");

    return await response.json();
  }
}
